import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatEuro, formatNumber, formatPercent } from "@/lib/format";
import KpiCard from "@/components/common/KpiCard";
import StatusBadge from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Landmark, Building, DoorOpen, Coins, Sparkles, PenLine, Save } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

export default function GroupOverviewTab() {
  const { activeRole } = useAuth();
  const canEdit = ["owner", "admin"].includes(activeRole);
  const [group, setGroup] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ group_name: "", group_description: "", group_notes: "" });

  const load = async () => {
    const [g, d] = await Promise.all([api.get("/group"), api.get("/group/dashboard")]);
    setGroup(g.data);
    setForm({
      group_name: g.data.group_name || "",
      group_description: g.data.group_description || "",
      group_notes: g.data.group_notes || "",
    });
    setDashboard(d.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try { await api.patch("/group", form); toast.success("Groupe mis à jour"); setEditing(false); load(); }
    catch (e) { toast.error("Enregistrement impossible"); }
  };

  if (!group || !dashboard) return <div className="text-muted-foreground text-sm">Chargement…</div>;

  return (
    <div className="space-y-8">
      {/* Group header */}
      <div className="card-elevated p-6 lg:p-8">
        {!editing ? (
          <div>
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-overline">Groupe</div>
                <h2 className="mt-1 font-serif text-3xl sm:text-4xl">{group.group_name || "—"}</h2>
                {group.group_description ? (
                  <p className="mt-3 text-muted-foreground leading-relaxed max-w-3xl">{group.group_description}</p>
                ) : (
                  <p className="mt-3 text-sm text-muted-foreground italic">Aucune description renseignée pour le moment.</p>
                )}
              </div>
              {canEdit && (
                <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="group-edit-btn">
                  <PenLine className="mr-2 h-4 w-4" /> Modifier
                </Button>
              )}
            </div>
            {group.group_notes && (
              <div className="mt-6 rounded-xl border border-accent/30 bg-accent/5 p-4 text-sm">
                <div className="text-overline mb-1">Notes internes</div>
                <div className="text-foreground leading-relaxed">{group.group_notes}</div>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <Label>Nom du groupe</Label>
              <Input value={form.group_name} onChange={(e) => setForm({ ...form, group_name: e.target.value })} data-testid="group-form-name" className="mt-1.5" />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea rows={3} value={form.group_description} onChange={(e) => setForm({ ...form, group_description: e.target.value })} data-testid="group-form-description" className="mt-1.5" />
            </div>
            <div>
              <Label>Notes internes</Label>
              <Textarea rows={2} value={form.group_notes} onChange={(e) => setForm({ ...form, group_notes: e.target.value })} className="mt-1.5" />
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => setEditing(false)}>Annuler</Button>
              <Button onClick={save} data-testid="group-form-submit"><Save className="mr-2 h-4 w-4" /> Enregistrer</Button>
            </div>
          </div>
        )}
      </div>

      {/* KPIs */}
      <section className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 lg:gap-6">
        <KpiCard name="group-entities" label="Entités du groupe" icon={Building}
                 value={formatNumber(dashboard.counts.entities)}
                 deltaLabel={`${dashboard.counts.entities_full} complètes · ${dashboard.counts.entities_reference} en référence`} />
        <KpiCard name="group-patrimoine" label="Patrimoine net estimé" icon={Landmark} tone="accent"
                 value={formatEuro(dashboard.values.patrimoine_net_estimated)} />
        <KpiCard name="group-real-estate" label="Valeur immobilière" icon={Landmark}
                 value={formatEuro(dashboard.values.real_estate_value)} />
        <KpiCard name="group-dividends" label="Dividendes perçus" icon={Coins}
                 value={formatEuro(dashboard.dividends.realized)}
                 deltaLabel={dashboard.dividends.planned ? `+ ${formatEuro(dashboard.dividends.planned)} prévus` : null} />
      </section>

      <section className="grid gap-6 lg:grid-cols-3">
        {/* Repartition */}
        <div className="card-elevated p-6 lg:col-span-2">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-overline">Consolidation</div>
              <h3 className="mt-1 font-heading text-xl">Répartition du patrimoine par société</h3>
            </div>
          </div>
          <div className="mt-6 space-y-3">
            {dashboard.repartition_by_company.length === 0 && (
              <div className="py-8 text-center text-muted-foreground text-sm">Aucune valeur renseignée.</div>
            )}
            {dashboard.repartition_by_company.map((r) => {
              const total = dashboard.values.patrimoine_net_estimated || 1;
              const pct = (r.value / total) * 100;
              return (
                <div key={r.company_id} data-testid={`repartition-row-${r.company_id}`}>
                  <div className="flex justify-between text-sm mb-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{r.denomination}</span>
                      <StatusBadge tone={r.management_mode === "reference" ? "warning" : "neutral"}>
                        {r.management_mode === "reference" ? "Référence" : "Complet"}
                      </StatusBadge>
                    </div>
                    <div className="font-mono text-xs text-muted-foreground">
                      {formatEuro(r.value)} · {formatPercent(pct)}
                    </div>
                  </div>
                  <div className="h-2 w-full rounded-full bg-secondary overflow-hidden">
                    <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${Math.min(pct, 100)}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Summary side card */}
        <div className="space-y-4">
          <div className="card-elevated p-6">
            <div className="text-overline">Loyers</div>
            <div className="mt-2 font-serif text-2xl">{formatEuro(dashboard.values.monthly_rent)}<span className="text-sm text-muted-foreground"> /mois</span></div>
            <div className="text-sm text-muted-foreground">{formatEuro(dashboard.values.annual_rent)} annualisés</div>
          </div>
          <div className="card-elevated p-6">
            <div className="text-overline">Immeubles & biens</div>
            <div className="mt-2 font-heading text-2xl flex items-center gap-2">
              <Building className="h-5 w-5 text-muted-foreground" /> {dashboard.counts.buildings}
            </div>
            <div className="mt-2 font-heading text-2xl flex items-center gap-2">
              <DoorOpen className="h-5 w-5 text-muted-foreground" /> {dashboard.counts.lots} lots
            </div>
          </div>
          <div className="card-elevated p-6 relative">
            <Sparkles className="absolute right-4 top-4 h-4 w-4 text-accent" />
            <div className="text-overline">Bientôt</div>
            <div className="mt-1 font-heading text-lg">Encours de crédits</div>
            <p className="text-sm text-muted-foreground mt-1">Disponible avec le module Finances.</p>
          </div>
        </div>
      </section>
    </div>
  );
}
